/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React from "react"
import enGB from "@duxton-web/ui/lib/locale/en_GB"
import idID from "@duxton-web/ui/lib/locale/id_ID"
import msMY from "@duxton-web/ui/lib/locale/ms_MY"
import thTH from "@duxton-web/ui/lib/locale/th_TH"
import viVN from "@duxton-web/ui/lib/locale/vi_VN"
import zhCN from "@duxton-web/ui/lib/locale/zh_CN"
import { storageHelper } from "utils/storage"
import { languageMapForLocalStorage } from "constants/i18n"

interface ReplaceRegexOption {
  // used to split the strings, e.g. ["This ", "<string>", " has been split."]
  splitRegex: RegExp
  // used to strip the strings after it has been split, e.g. "<string>" -> "string"
  stripRegex: RegExp
}

export const replaceRegexes = {
  arrowBrackets: {
    // format: <x> or <stuff>, but not <>
    // you can check the regex match here https://regex101.com/r/VCsvBH/4
    splitRegex: /(<\w+>|<\w+[.\w+]+>)/g,
    stripRegex: /<|>/g,
  },
  aTag: {
    // format: <a>blargh</a>
    splitRegex: /(<a>.+<\/a>)/g,
    stripRegex: /<a>|<\/a>/g,
  },
  dollars: {
    // format: $placeholderHere
    splitRegex: /(\$\w+)/g,
    stripRegex: /\$/g,
  },
} as const

export const replaceWithReactChild = (
  str: string,
  replacements: {
    [key: string]: React.ReactNode
  },
  opts: {
    replaceRegex?: ReplaceRegexOption
  } = {},
): React.ReactNode => {
  const { replaceRegex = replaceRegexes.arrowBrackets } = opts
  const parts = str.split(replaceRegex.splitRegex)
  return parts.map(part => {
    if (!replaceRegex.splitRegex.test(part)) {
      return part
    }
    return replacements[part.replace(replaceRegex.stripRegex, "")] ?? part
  })
}

/** This is different `replaceRegexes` due to the circular brackets */
export enum replaceRegexIntoLinkType {
  // format: <link>blargh</link>
  linkTag = "<link>(.+)<\\/link>",
  // format: <a>blargh</a>
  aTag = "<a>(.+)<\\/a>",
}

export const replaceRegexIntoLink = (
  str: string,
  destination: string,
  replaceRegexType: typeof replaceRegexIntoLinkType[keyof typeof replaceRegexIntoLinkType],
): string => {
  const replaceRegex = new RegExp(replaceRegexType)
  const parts = str.match(replaceRegex) || str
  if (parts.length >= 2) {
    return str.replace(
      replaceRegex,
      `<a href="${destination}" target="_blank" rel="noreferrer">${parts[1]}</a>`,
    )
  }
  return str
}

// use ReactHook useLocaleContext("locale") in your react component to get localeCode
export const getDuxtonLocale = (localeCode: string) => {
  switch (localeCode) {
    case "en": {
      return enGB
    }
    case "id": {
      return idID
    }
    case "ms": {
      return msMY
    }
    case "th": {
      return thTH
    }
    case "vi": {
      return viVN
    }
    case "zh": {
      return zhCN
    }
    default: {
      return enGB
    }
  }
}

export const getAcceptLanguageCode = (
  useLongFormat: boolean = true,
): string => {
  const localStorageLang = storageHelper.getLangFromLS()

  if (!localStorageLang) {
    return useLongFormat
      ? languageMapForLocalStorage.EN.longFormat
      : languageMapForLocalStorage.EN.shortFormat
  }

  return useLongFormat
    ? languageMapForLocalStorage[localStorageLang].longFormat
    : languageMapForLocalStorage[localStorageLang].shortFormat
}
